import type { CanvasKit, Image as SkImage } from 'canvaskit-wasm';
import { readableMediaUrl } from '../services/mediaRead';

/**
 * The frames of the clips a document holds: one `<video>` per video asset, muted, driven by
 * the page's clock.
 *
 * The engine says where each clip is (`Pose.videoAt`); this makes the video be there.
 *  - At rest a clip shows its first frame: a snapshot taken once, when the video has one, so
 *    a tile cached at rest can never pick up a later frame.
 *  - While a page plays, the video plays too, in step with the page (a seek when it drifts
 *    or wraps round), and each drawing uploads the frame it is showing — a fresh image per
 *    frame, straight from the video to the GPU, never through the CPU.
 *  - Held on an instant (paused, or the playhead dragged), the video seeks there and the
 *    page is drawn again once it has.
 */
export class ClipFrames {
  private readonly clips = new Map<string, Clip>();
  /** Whether time is running: a page playing rather than held. Set by the drawing loop. */
  private flowing = false;

  constructor(
    private readonly ck: CanvasKit,
    /** Something new to draw: a first frame arrived, or a seek landed. */
    private readonly onChanged: () => void,
  ) {}

  /** Time runs (a page plays) or stands (held, or nothing playing): videos follow. */
  setFlowing(flowing: boolean): void {
    if (flowing === this.flowing) return;
    this.flowing = flowing;
    if (!flowing) for (const clip of this.clips.values()) clip.video.pause();
  }

  /** Why a clip is not drawing, for the editor to show. */
  errorFor(assetId: string): string | null {
    return this.clips.get(assetId)?.failed ?? null;
  }

  /** The frame to draw for `assetId`: its first at rest, the one `at` seconds in while playing. */
  get(assetId: string, src: string, at: number | undefined, mediaAssetId?: string): SkImage | null {
    const clip = this.clips.get(assetId) ?? this.open(assetId, src, mediaAssetId);
    if (clip.failed || at === undefined || !clip.still) return clip.still;
    const { video } = clip;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return clip.still;
    if (this.flowing) {
      if (video.paused) {
        video.currentTime = at;
        video.play().catch(() => undefined);
      } else if (Math.abs(video.currentTime - at) > DRIFT && !video.seeking) {
        // Round again, or fallen behind the page: back to where the page says it is.
        video.currentTime = at;
      }
    } else {
      if (!video.paused) video.pause();
      if (Math.abs(video.currentTime - at) > HELD && !video.seeking) {
        video.currentTime = at;
        video.addEventListener('seeked', this.onChanged, { once: true });
      }
    }
    clip.frame?.delete();
    clip.frame = this.ck.MakeLazyImageFromTextureSource(video, {
      width: video.videoWidth,
      height: video.videoHeight,
      colorType: this.ck.ColorType.RGBA_8888,
      alphaType: this.ck.AlphaType.Unpremul,
    });
    return clip.frame;
  }

  dispose(): void {
    for (const clip of this.clips.values()) {
      clip.video.pause();
      clip.video.removeAttribute('src');
      clip.video.load();
      clip.frame?.delete();
      clip.still?.delete();
      clip.bitmap?.close();
    }
    this.clips.clear();
  }

  private open(assetId: string, src: string, mediaAssetId?: string): Clip {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    const clip: Clip = { video, still: null, frame: null, bitmap: null, failed: null };
    this.clips.set(assetId, clip);
    video.addEventListener(
      'loadeddata',
      () => {
        createImageBitmap(video)
          .then((bitmap) => {
            if (this.clips.get(assetId) !== clip) return bitmap.close();
            clip.bitmap = bitmap;
            clip.still = this.ck.MakeLazyImageFromTextureSource(bitmap, {
              width: bitmap.width,
              height: bitmap.height,
              colorType: this.ck.ColorType.RGBA_8888,
              alphaType: this.ck.AlphaType.Unpremul,
            });
            this.onChanged();
          })
          .catch((error: unknown) => this.fail(clip, error));
      },
      { once: true },
    );
    video.addEventListener('error', () => {
      if (this.clips.get(assetId) === clip) this.fail(clip, clipLoadError(video));
    }, { once: true });
    void readableMediaUrl(src, mediaAssetId).then(url => {
      if (this.clips.get(assetId) === clip) video.src = url;
    }).catch(error => {
      if (this.clips.get(assetId) === clip) this.fail(clip, error);
    });
    return clip;
  }

  private fail(clip: Clip, error: unknown): void {
    clip.failed = (error as Error).message;
    console.warn(`Could not load a clip: ${clip.failed}`);
    this.onChanged();
  }
}

interface Clip {
  readonly video: HTMLVideoElement;
  /** The first frame, for a clip at rest. */
  still: SkImage | null;
  /** The frame last handed out while playing; deleted when the next one is. */
  frame: SkImage | null;
  bitmap: ImageBitmap | null;
  failed: string | null;
}

/** How far a playing video may wander from the page's clock before it is sent back, in seconds. */
const DRIFT = 0.25;
/** How near a held video must be to the instant it is held on. */
const HELD = 1 / 60;

/**
 * A video's length and size, read from the file itself — what a clip needs to be placed and
 * checked against the cap. Drive's list does not say how long a video is.
 */
export function readClip(src: string): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    const timer = setTimeout(() => {
      done();
      reject(new Error('The video took too long to load. Try again.'));
    }, 30_000);
    const done = () => {
      clearTimeout(timer);
      video.removeEventListener('loadedmetadata', loaded);
      video.removeEventListener('error', failed);
      video.removeAttribute('src');
      video.load();
    };
    const loaded = () => {
      const facts = { duration: video.duration, width: video.videoWidth, height: video.videoHeight };
      done();
      if (!Number.isFinite(facts.duration) || facts.duration <= 0) reject(new Error('it has no length the browser can read'));
      else resolve(facts);
    };
    const failed = () => {
      const error = clipLoadError(video);
      done();
      reject(error);
    };
    video.addEventListener('loadedmetadata', loaded);
    video.addEventListener('error', failed);
    video.src = src;
  });
}

function clipLoadError(video: HTMLVideoElement): Error {
  const code = video.error?.code;
  if (code === 2) return new Error('The video could not be downloaded. Check your connection and try again.');
  if (code === 3) return new Error('The video could not be decoded. Try an MP4 made with H.264.');
  if (code === 4) return new Error('The video source could not be loaded or its format is not supported.');
  return new Error(video.error?.message || 'The video could not be loaded.');
}
